import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    ClubNavItem,
    Search,
    SeasonNavItem
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/nav-items';
import {
    getPredictorTrackingLabel,
    getIsAFLW
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/table/utils';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { BrownlowPredictor } from './BrownlowPredictor';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';
import { setLeaderboardStateItem } from 'common/store/modules/player-awards/leaderboard';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown">{children}</div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

/**
 * Fires `onChange` whenever `value` changes, skipping the initial mount
 *
 * @param {*} value
 * @param {Function} onChange
 */
const useTrackOnChange = (value, onChange) => {
    const isInitialRender = useRef(true);

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }

        onChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
};

export const BPLeaderboard = ({
    seasonsToShow,
    seasonId,
    compId,
    seasonPid,
    title
}) => {
    const classPrefix = 'brownlow-leaderboard-predictor brownlow-leaderboard';
    const windowSize = useWindowSizeV2();

    const nav = useSelector(navSelector);
    const dispatch = useDispatch();
    const options = useCompetitionNavigationOptions(nav);

    const leaderboardState = useSelector(leaderboardSelector);

    const isAFLW = getIsAFLW(seasonPid);
    const compLabel = getPredictorTrackingLabel(isAFLW, '').trim();

    options.defaultSeason = seasonPid ?? seasonPid;
    options.seasonsToShow = seasonsToShow ?? seasonsToShow;

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    const leaderboardRef = useRef();

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: {
                    competitionId: Number.parseInt(compId),
                    seasonId: Number.parseInt(seasonId)
                }
            })
        );
    }, [dispatch]);

    /**
     * Fires a single-dimension apply_filters event for a given filterId,
     * with scalar filter_type/filter_value (not the arrays convertFiltersValues
     * returns), matching the GA spec's "one filter interacted with per event" shape
     *
     * @param {string} filterId - The FILTER_TYPE id to resolve the value for, e.g. 'season'/'club'
     * @param {string} filterTypeName - The `filter_type` value to send, e.g. 'year'/'teams'
     */
    /* eslint-disable camelcase */
    const trackFilterChange = (filterId, filterTypeName) => {
        const { values } = PULSE.app.analytics.convertFiltersValues(
            [filterId],
            { ...nav, ...leaderboardState },
            options,
            nav?.season?.id,
            { [filterId]: filterTypeName }
        );

        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.FILTERS.APPLY,
            {
                eventData: {
                    component_name: getPredictorTrackingLabel(
                        isAFLW,
                        'player filters'
                    ),
                    component_type: 'player filters',
                    filter_type: filterTypeName,
                    filter_value: values?.[0]?.[0]
                }
            }
        );
    };
    /* eslint-enable camelcase */

    /**
     * Tracks the info icon click as select_cta
     */
    /* eslint-disable camelcase */
    const trackInfoIconClick = () => {
        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.SELECT.CTA,
            {
                eventData: {
                    link_text: 'info',
                    link_url: window.location.pathname,
                    outbound: false,
                    component_name: getPredictorTrackingLabel(
                        isAFLW,
                        'predictor info'
                    ),
                    component_type: 'info dropdown',
                    component_content_type: compLabel
                }
            }
        );
    };
    /* eslint-enable camelcase */

    /**
     * Fires apply_filters immediately when the season/team filters change
     * (unlike the site-wide useFiltersTracking pattern, which batches on
     * beforeunload - this widget needs the event to fire straight away)
     */
    useTrackOnChange(nav?.season?.id, () =>
        trackFilterChange('season', 'year')
    );
    useTrackOnChange(leaderboardState?.team?.id, () =>
        trackFilterChange('club', 'teams')
    );

    const getInitialSeasonId = () => {
        return options.defaultSeason
            ? options.seasons?.find(
                  (season) => season.providerId === options.defaultSeason
              )?.id
            : options.seasons?.[0]?.id;
    };

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav, team: leaderboardState?.team },
        initialValues: {
            seasonId: getInitialSeasonId(),
            team: -1
        }
    });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId: compId,
            seasonId: getInitialSeasonId()
        },
        onAfterReset: () => {
            dispatch(
                setLeaderboardStateItem({
                    item: 'team',
                    value: {
                        id: -1
                    }
                })
            );
        }
    });

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const selectedSeasonPid = useMemo(() => {
        return options?.seasons?.find((season) => season.id === nav.season?.id)
            ?.providerId;
    }, [nav, options]);

    return (
        <div ref={leaderboardRef} className={classPrefix}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <div className="player-awards-wrapper">
                            <SeasonNavItem
                                classes="shrink bp-season-nav-item"
                                seasonsToShow={seasonsToShow}
                                seasonId={seasonId}
                                compId={compId}
                                title={title}
                            />
                            <ClubNavItem
                                title={title}
                                classes={'clubnavitem'}
                            />
                        </div>
                        {hasChangedFromDefaults && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <StatsSection.InfoButton
                                    tooltipContent="BPLeaderboardTooltip"
                                    className="p-none"
                                    seasonPid={selectedSeasonPid}
                                    onClick={trackInfoIconClick}
                                />
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults && showResponsiveItems ? (
                        <StatsSection.NavFilterItems hideReset>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}

                    <StatsSection.SettingsButton>
                        <div className={`${classPrefix}-nav__extras`}>
                            <Search trackingComponentName={compLabel} />
                        </div>
                        <StatsSection.InfoButton
                            tooltipContent="BPLeaderboardTooltip"
                            className="p-none"
                            seasonPid={selectedSeasonPid}
                            onClick={trackInfoIconClick}
                        />
                        <SettingsButton />
                    </StatsSection.SettingsButton>

                    {showResponsiveItems ? (
                        <StatsSection.SearchBarMobile>
                            <div className={`${classPrefix}-nav__extras`}>
                                <Search trackingComponentName={compLabel} />
                            </div>
                        </StatsSection.SearchBarMobile>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <BrownlowPredictor seasonId={seasonId} />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

BPLeaderboard.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonId: PropTypes.string,
    compId: PropTypes.string,
    seasonPid: PropTypes.string,
    title: PropTypes.string
};
